import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import Loading from '../../../molecules/Loading'
import SydLabel, { hookFormFieldErrorAdapter } from '../../../commonDesign/SydLabel'
import SydInput from '../../../commonDesign/SydInput'
import { useRedtailDomainQuery } from '../../../../api/redtail'
import SydButton from '../../../commonDesign/Button'
import { familyNameDefault } from '../helpers'
import ExistingFamilyCard from './ExistingFamilyCard'
import ConflictCallout from './ConflictCallout'

const useStyles = makeStyles({
  errorRow: { color: 'var(--color-decline-500)' }
})

function RedtailFamilyPreview ({ contact, control, conflict, currentClientName, onResolved }) {
  const classes = useStyles()
  const familyQuery = useRedtailDomainQuery('getContactFamily', { contactId: contact?.id }, {
    enabled: !!contact?.id
  })

  const family = familyQuery.data?.family ?? null
  const hasFamily = !!family?.id

  useEffect(() => {
    if (!familyQuery.isLoading && !familyQuery.error) {
      onResolved?.({ hasFamily, family, contact })
    }
  }, [familyQuery.isLoading, familyQuery.error, hasFamily, family, contact, onResolved])

  const defaultName = useMemo(() => familyNameDefault(contact), [contact])

  if (!contact) return null
  if (familyQuery.isLoading) return <Loading />
  if (familyQuery.error) {
    return (
      <div className={clsx(classes.errorRow, 'font-size-sm')}>
        Could not load family information.<br />
        <SydButton
          variant='primary'
          priority='warning'
          size='sm'
          type='button'
          onClick={() => familyQuery.refetch()}
        >
          Retry
        </SydButton>
      </div>
    )
  }

  if (hasFamily) {
    return (
      <>
        <ExistingFamilyCard contact={contact} family={family} />
        <ConflictCallout conflict={conflict} currentClientName={currentClientName} />
      </>
    )
  }

  return (
    <Controller
      name='familyName'
      control={control}
      defaultValue={defaultName}
      rules={{ required: 'Family name is required', maxLength: { value: 120, message: 'Max 120 characters' } }}
      render={({ field, fieldState }) => (
        <SydLabel label='Create new family' required error={hookFormFieldErrorAdapter(fieldState)}>
          <SydInput
            value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
            placeholder={defaultName}
            autoFocus
          />
          <div className='font-size-xs opacity-lowlighted mt-1'>
            {contact.full_name || `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim()} will be set as Head of Household.
          </div>
        </SydLabel>
      )}
    />
  )
}

RedtailFamilyPreview.propTypes = {
  contact: PropTypes.object,
  control: PropTypes.object.isRequired,
  conflict: PropTypes.object,
  currentClientName: PropTypes.string,
  onResolved: PropTypes.func
}

export default RedtailFamilyPreview
